import { useToggle } from 'react-use';
import { Common } from '@thecvlb/design-system/lib/src';
import classNames from 'classnames';
import { nanoid } from 'nanoid';

import InsuranceMedicationCard from '../InsuranceMedicationCard';

import { Medication } from 'models/medication.types';

interface InsuranceMedicationsListProps {
  loading?: boolean;
  medications: Medication[];
  onSelect: (id: string) => void;
  status: string;
}

const InsuranceMedicationsList = ({
  medications,
  onSelect,
  status,
  loading
}: InsuranceMedicationsListProps) => {
  const [isCashPayOptions, toggle] = useToggle(false);
  const filteredMedications = medications.filter((m) =>
    isCashPayOptions || status === 'error' ? 'oldPrice' in m : 'status' in m
  );

  return (
    <div className="flex flex-col items-center gap-6">
      {medications.length > 1 && (
        <div className="mt-0.5 flex items-center justify-center gap-4 text-inherit">
          <button
            className={classNames('text-gray', {
              'font-bold text-primary-700': !isCashPayOptions
            })}
            data-testid={`with_insurance_btn_${isCashPayOptions ? 'disabled' : 'enabled'}`}
            onClick={() => toggle(false)}
          >
            With insurance
          </button>
          <Common.Toggle
            checked={isCashPayOptions}
            color="primary"
            dataTestId={`toggle_${isCashPayOptions ? 'no_insurance' : 'with_insurance'}`}
            isPermanentColor
            onClick={toggle}
          />

          <button
            className={classNames('text-gray', { 'font-bold text-primary-700': isCashPayOptions })}
            data-testid={`no_insurance_btn_${isCashPayOptions ? 'enabled' : 'disabled'}`}
            onClick={() => toggle(true)}
          >
            No insurance
          </button>
        </div>
      )}
      <div className="flex flex-col gap-2 self-stretch">
        {isCashPayOptions && (
          <span className="self-start">
            <span className="font-bold">Cash-pay</span> (no insurance necessary)
          </span>
        )}
        {filteredMedications.map((medication) => (
          <InsuranceMedicationCard
            {...medication}
            key={nanoid()}
            loading={loading}
            onSelect={onSelect}
          />
        ))}
      </div>
      {!isCashPayOptions && !['error', 'pending'].includes(status) && (
        <div className="flex flex-col gap-1">
          <Common.Button className="mx-auto" color="white-alt" fullWidthOnMobile onClick={toggle}>
            More options...
          </Common.Button>
          <span className="text-center">
            Not seeing any good options with insurance? We have great cash-pay options as well.
          </span>
        </div>
      )}
    </div>
  );
};

export default InsuranceMedicationsList;
